import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog, AppBar, Toolbar, IconButton, Typography, Slide, MenuItem,
  DialogContent, Button, Grid, TextField, Box,
  Table, TableHead, TableBody, TableRow, TableCell,
  Snackbar, Alert, DialogTitle, DialogActions, FormControlLabel, Checkbox,
  Accordion, AccordionSummary, AccordionDetails, Backdrop, CircularProgress,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EstimateDetailContent from './EstimateDetailContent';
import { AuthContext } from './util/AuthContext';
import MaterialPage from '../pages/MaterialPage';
import AddClientModal from './addClientDialog';

// 풀스크린 슬라이드 트랜지션 설정
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EstimateWriteDialog = ({ open, onClose, estimate }) => {
  // 고객사 목록
  const [clientOptions, setClientOptions] = useState([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  // 고객사 모달 상태
  const [openAddClient, setOpenAddClient] = useState(false);
  const [pendingClient, setPendingClient] = useState('');
  const [inputValue, setInputValue] = useState('');
  // 로그인한 유저 정보 상태
  const { user} = useContext(AuthContext);
  // 자재 선택 모달 상태
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  // 자재 리스트 상태
  const [materialRows, setMaterialRows] = useState([]);
  // 모달 안에서 체크한 자재들 (MaterialPage → onSelectionChange 으로 받음)
  const [modalSelectedMaterials, setModalSelectedMaterials] = useState([]);
  // 알림 상태
  const [alertOpen, setAlertOpen] = useState(false);
  // **수정 확인 다이얼로그** 상태
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // 로딩 상태
  const [isSaving, setIsSaving] = useState(false);
  // Accordion 확장 상태
  const [expanded, setExpanded] = useState(true);
  const handleAccordionToggle = () => setExpanded(prev => !prev);
  const [expanded2, setExpanded2] = useState(false);
  const handleAccordion2Toggle = () => setExpanded2(prev => !prev);
  const emptyForm = {
    company: 'HS',          // HS or BPS
    clientCompany: '',
    contactPerson: user.userNick,
    phoneNumber: user.userPhone,
    email: user.userEmail,
    projectName: '',
    deliveryDate: '공사발주 후 2주',
    validityPeriod: '',
    separateVat: false,
    estimateNumber: '',
    includeProfit: false,
    uploadDate: '',
    etc: '',
    negoAmount: 0,
  }
  // 폼 값 state
  const [formValues, setFormValues] = useState(emptyForm);

  // 입력값이 바뀔 때 호출
  const handleInputChange = (event, newInput) => {
    setInputValue(newInput);
    handleChange('clientCompany')({ target: { value: newInput } });
  };

  // 옵션 필터링 (+ “추가” 아이템 삽입)
  const filterOptions = (options, { inputValue }) => {
    const filtered = options.filter(opt =>
      opt.toLowerCase().includes(inputValue.toLowerCase())
    );
    if (
      inputValue !== '' &&
      !options.some(opt => opt.toLowerCase() === inputValue.toLowerCase())
    ) {
      filtered.push(`__add__${inputValue}`);
    }
    return filtered;
  };

  // 옵션 선택/클릭 시
  const handleOptionSelect = (event, newValue) => {
    if (typeof newValue === 'string' && newValue.startsWith('__add__')) {
      const nameToAdd = newValue.replace('__add__', '');
      openAddModal(nameToAdd);
    } else {
      handleChange('clientCompany')({ target: { value: newValue || '' } });
    }
  };

  // “추가” 모달 열기
  const openAddModal = (name) => {
    setPendingClient(name);
    setOpenAddClient(true);
  };

  // 모달 닫기
  const closeAddModal = () => {
    setOpenAddClient(false);
    setPendingClient('');
  };

  // 모달에서 “추가” 버튼 클릭 시
  const confirmAddClient = () => {
    setClientOptions(prev => [...prev, pendingClient]);
    handleChange('clientCompany')({ target: { value: pendingClient } });
    closeAddModal();
  };

  // 드롭다운 옵션 렌더링
  const renderOption = (props, option) => {
    if (typeof option === 'string' && option.startsWith('__add__')) {
      const name = option.replace('__add__', '');
      return (
        <li {...props} key="add-client">
          <Button
            startIcon={<AddIcon />}
            fullWidth
            onMouseDown={e => e.preventDefault()} // blur 방지
            onClick={() => openAddModal(name)}
          >
            고객사 추가 "{name}"
          </Button>
        </li>
      );
    }
    return (
      <li {...props} key={option}>
        {option}
      </li>
    );
  };
  
  // 드롭다운 열릴 때 한 번만 호출(고객사 목록 로드)
  const loadClientOptions = async () => {
    if (optionsLoaded) return;
    try {
      const res = await fetch('/api/clients');
      const data = await res.json(); // [{id, clientName}, …]
      // name만 추출
      setClientOptions(data.map((c) => c.clientName));
      setOptionsLoaded(true);
    } catch (err) {
      console.error('고객사 목록 로드 실패', err);
    }
  };

  // **포커스 아웃 시** 목록에 없는 값이면 비우는 핸들러
  const handleBlur = () => {
    if (inputValue && !clientOptions.includes(inputValue)) {
      setInputValue('');
      handleChange('clientCompany')({ target: { value: '' } });
    }
  };




  // 필수입력 필드
  // open 이 true 로 바뀔 때마다 estimate 에서 뽑아서 한 번만 세팅
  useEffect(() => {
    if(!open) return;

    if (estimate) {
      // “수정” 모드: estimate 에서 폼, 자재 세팅
      setFormValues({
        company:       estimate.company       || 'HS',
        contactPerson: estimate.contactPerson || '',
        phoneNumber:   estimate.phoneNumber   || '',
        email:         estimate.email         || '',
        projectName:   estimate.projectName   || '',
        deliveryDate:  estimate.deliveryDate  || '',
        validityPeriod: estimate.validityPeriod || '',
        separateVat:   estimate.separateVat   || false,
        includeProfit: estimate.includeProfit || false,
        clientCompany: estimate.clientCompany || '',
        estimateNumber: estimate.estimateNumber || '',
        uploadDate: estimate.uploadDate || '',
        negoAmount: estimate.negoAmount || 0,
        etc: estimate.etc || '',
        }
      );
      setMaterialRows(
        (estimate.estimateMaterials ?? []).map(mat => ({
          // UI/Drag/DataGrid 전용 키 → 항상 materialId
          id:                mat.materialId,
          materialId:        mat.materialId,
          materialName:      mat.materialName,
          materialUnit:      mat.materialUnit,
          materialSpec:      mat.materialSpec,
          materialPrice:     mat.materialPrice,
          quantity:          mat.quantity,
          sortOrder:         mat.sortOrder,
          note:              mat.note || '',
        }))
      );
    }else{
      // “신규” 모드: 폼 초기화
      setFormValues(emptyForm);
      setMaterialRows([]);
    }
  }, [open, estimate]);

  // 부가세 별도 체크 핸들러
  const handleSeparateVatChange = () => {
    setFormValues(prev => ({ ...prev, separateVat: !prev.separateVat }));
  };
  
  // 이윤 포함 체크 핸들러
  const handleIncludeProfitChange = () => {
    setFormValues(prev => ({ ...prev, includeProfit: !prev.includeProfit }));
  };

  // 폼 값 변경 핸들러
  const handleChange = field => e => {
    setFormValues(v => ({ ...v, [field]: e.target.value }));
  };

  // 숫자 전용 onChange 핸들러 정의
  const handleNumericChange = field => e => {
    const { value } = e.target;
    // 빈 문자열(지우기) 또는 숫자만 허용
    if (value === '' || /^\d+$/.test(value)) {
      setFormValues(v => ({ ...v, [field]: value }));
    }
  };
  
  

  // “자재 선택” 버튼 클릭
  const handleMaterialClick = () => {
    const init = materialRows.map(r => ({
      id:            r.materialId,    // 여기 id가 materialId 로 들어갑니다
      name:          r.materialName,
      specifications:r.materialSpec,
      unit:          r.materialUnit,
      unitPrice:     r.materialPrice,
    }));
    setModalSelectedMaterials(init);
    setMaterialDialogOpen(true);
  };

  // 모달 닫기 (취소)
  const handleMaterialDialogClose = () => {
    setMaterialDialogOpen(false);
  };

  // 모달 확인 → modalSelectedMaterials 를 materialRows 에 병합
  const handleMaterialDialogConfirm = () => {
    setMaterialDialogOpen(false);
    
    setMaterialRows(prevRows => {
      // 모달에서 최종 선택된 materialId 배열
      const selectedIds = modalSelectedMaterials.map(m => m.id);

      // 2) 기존에 있던 자재 중, 여전히 선택된 것만—원래 순서대로
      const kept = prevRows.filter(r => selectedIds.includes(r.materialId));

      // 3) 새로 추가된 자재(즉, prevRows 에 없던 것들만)
      const added = modalSelectedMaterials
      .filter(m => !prevRows.some(r => r.materialId === m.id))
      .map((m, idx) => ({
        id:                m.id,            // UI key
        materialId:        m.id,
        materialName:      m.name,
        materialUnit:      m.unit,
        materialSpec:      m.specifications || '',
        materialPrice:     m.unitPrice,
        quantity:          1,
        sortOrder:         kept.length + idx + 1,
        note:              '',
      }));

      // 4) 합치기: [원래 순서 유지된 kept] + [새로 붙은 added]
      return [...added, ...kept ];
      
    });
  };


  // 자재 입력 필드 값 변경 핸들러
  const handleMaterialChange = (id, field) => e => {
    const value = e.target.value;
    setMaterialRows(rows =>
      rows.map(r =>
        r.id === id
          ? { ...r, [field]: field==="quantity"||field==="materialPrice" ? Number(value) : value }
          : r
      )
    );
  };

  // 2) Drag end → 순서 재설정
  const handleDragEnd = result => {
    if (!result.destination) return;
    const items = Array.from(materialRows);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setMaterialRows(items);
  };

  // materialRows 중 id가 일치하는 행을 제거
  const handleMaterialDelete = (id) => {
    setMaterialRows(prev => prev.filter(r => r.id !== id));
  };



  // 저장 핸들러
  const saveEstimate = async (treatAsNew = false) => {

    setIsSaving(true); // 로딩 시작

    // payload 구성
    const payload = {
      // id가 있으면 편집, 없으면 신규
      id: estimate?.id ?? null,
      createdByName: user?.username ?? null,
      createdByNick: user?.userNick ?? null,
      company:       formValues.company,
      clientCompany: formValues.clientCompany,
      contactPerson: formValues.contactPerson,
      phoneNumber:   formValues.phoneNumber,
      email:         formValues.email,
      projectName:   formValues.projectName,
      // 백엔드에서 estimateNumber 자동 생성/증번 처리하므로 클라이언트에서는 기존값 넘겨주기만
      estimateNumber: formValues.estimateNumber,
      separateVat:   formValues.separateVat,
      includeProfit: formValues.includeProfit,
      deliveryDate:  formValues.deliveryDate,
      validityPeriod: formValues.validityPeriod,
      tempSave:       false,
      negoAmount:     formValues.negoAmount,
      etc:            formValues.etc,
      // 자재 리스트
      estimateMaterials: materialRows.map((row, idx) => ({
        id:           row.id ?? null,
        materialName: row.materialName,
        materialUnit: row.materialUnit,
        materialSpec: row.materialSpec ?? '',
        materialPrice:row.materialPrice,
        materialId:   row.materialId,
        quantity:     row.quantity,
        note:         row.note ?? '',
        sortOrder:    idx + 1,
        estimateId:   estimate?.id ?? null,
      }))
    };

    // 저장 백엔드 호출
    try {
      const res = await fetch('/api/estimates', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`저장 실패: ${res.status}`);
      const savedDto = await res.json();
      console.log('저장 완료:', savedDto);
      // 저장 후, 부모(DraggableDialog)에게 알려주고 모달 닫기
      onClose(savedDto);
    } catch (err) {
      console.error(err);
      alert('저장 중 오류가 발생했습니다.');
    }finally{
      setIsSaving(false); // 로딩 종료
    }
  };

  // 저장 버튼 핸들러 수정 모드면 확인 다이얼로그, 아니면 즉시 저장
  const handleSaveClick = () => {

    // 자재 최소 1개이상 입력 검증
    if(!materialRows || materialRows.length < 1) {
      setAlertOpen(true);
      return;
    }

    // 필수 입력값 검증
    const { clientCompany, contactPerson, phoneNumber, email, projectName, deliveryDate, validityPeriod } = formValues;
    if (
      !clientCompany.trim() ||
      !contactPerson.trim() || 
      !phoneNumber.trim() ||
      !email.trim() ||
      !projectName.trim() ||
      !deliveryDate.trim() ||
      !validityPeriod.trim()
    ) {
      alert("모든 필수 입력 필드를 채워주세요.");
      return; // 빈 칸이 있으면 저장 로직 중단
    }

    setConfirmDialogOpen(true);
  };

  // 다이얼로그 “확인” 클릭 시: 새로운 견적으로 저장
  const handleConfirm = () => {
    setConfirmDialogOpen(false);
    saveEstimate(true);
  };

  return (
    <>

    {/* 로딩 백드롭 모달보다 Index값이 1 높아야 함 */}
    <Backdrop open={isSaving} sx={{ zIndex: theme => theme.zIndex.modal + 1, color: '#fff'}}>
      <CircularProgress color="inherit"/>
    </Backdrop>


    <Dialog fullScreen    open={open}    onClose={onClose}    TransitionComponent={Transition} >
      {/* 자재 입력 없을 때 경고 */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
       anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity="warning"
          sx={{ width: '100%' }}
        >
          모든 입력필드와 자재를 한개이상 입력하세요
       </Alert>
      </Snackbar>
      
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => onClose(null)} disabled={isSaving} aria-label="close"><CloseIcon /></IconButton>
          <Typography variant="h6" sx={{ ml: 2, flex: 1 }} component="div"> 견적서 작성 </Typography>
          <Button 
            autoFocus 
            color="inherit" 
            onClick={handleSaveClick}
            disabled={isSaving}          // 저장 중에는 닫기 비활성화
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              minWidth: '120',
            }}
          > 
           저장 
          </Button>
        </Toolbar>
      </AppBar>

      <DialogContent>
        {/* 미리보기와 입력필드 컨테이너 */}
        <Box
          sx ={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexWrap: 'wrap',     // 창이 좁아지면 wrap
            gap: 10,
            pb : 2,
          }}
        >
          {/* 왼쪽: 미리보기 */}
          <Box
            sx={{
              width: '210mm',
              height: '297mm',
              border: '2px solid black',
              p: 0,
              borderCollapse: 'collapse',
              flexShrink: 0,           // 축소 방지
            }}
          >
            <EstimateDetailContent
              id={estimate?.id}
              formValues={formValues}
              materialRows={materialRows}
            />
          </Box>

          {/* 오른쪽: 입력 폼 */}
          <Box sx={{ 
            p: 2,
            minWidth : '660px'
          }}>
            <Accordion expanded={expanded} onChange={handleAccordionToggle} sx={{ mb: 2 }} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant="subtitle1">필수 입력 필드</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.separateVat}
                      onChange={handleSeparateVatChange}
                    />
                  }
                  label="부가세 포함"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.includeProfit}
                      onChange={handleIncludeProfitChange}
                    />
                  }
                  label="이윤 포함"
                />
                <br/><br/>
  
                {/* 1행: 공급사 + 고객사 */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <TextField
                      select
                      name="company"
                      fullWidth
                      label="공급사"
                      value={formValues.company}
                      onChange={handleChange('company')}
                      required
                      error={formValues.company === ''}
                    >
                      <MenuItem value="HS">HS</MenuItem>
                      <MenuItem value="BPS">BPS</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                  <Autocomplete
                    freeSolo                      // 자동완성 기능 활성화
                    openOnFocus                  // 포커스 시 자동으로 드롭다운 열기
                    options={clientOptions}       // 드롭다운 옵션
                    inputValue={inputValue}        // 입력값 관리
                    value={formValues.clientCompany}  // 선택된 값
                    onOpen={loadClientOptions}      // 드롭다운 열릴 때 호출
                    onInputChange={handleInputChange}  // 입력 변경 시 호출
                    filterOptions={filterOptions}  // 필터링 함수
                    onChange={handleOptionSelect}  // 선택 시 호출
                    renderOption={renderOption}    // 옵션 렌더링 함수
                    renderInput={params => (
                      <TextField
                        {...params}
                        name="clientCompany"
                        label="고객사"
                        fullWidth
                        required
                        error={formValues.clientCompany === ''}
                        onBlur={handleBlur}
                      />
                    )}
                  />
                  </Grid>
                </Grid>

                {/* 2행: 담당자 + 연락처 + 이메일 */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <TextField
                      name="contactPerson"
                      fullWidth
                      label="담당자"
                      value={formValues.contactPerson}
                      onChange={handleChange('contactPerson')}
                      required
                      error={formValues.contactPerson === ''}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      name="phoneNumber"
                      fullWidth
                      label="연락처"
                      value={formValues.phoneNumber}
                      onChange={handleChange('phoneNumber')}
                      required
                      error={formValues.phoneNumber === ''}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      name="email"
                      fullWidth
                      label="이메일"
                      value={formValues.email}
                      onChange={handleChange('email')}
                      required
                      error={formValues.email === ''}
                    />
                  </Grid>
                </Grid>

                {/* 3행: 공사명 */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    name="projectName"
                    fullWidth
                    label="공사명"
                    value={formValues.projectName}
                    onChange={handleChange('projectName')}
                    required
                    error={formValues.projectName === ''}
                  />
                </Box>

                {/* 4행: 납기일 + 견적 유효기간 */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <TextField
                      name="deliveryDate"
                      fullWidth
                      label="납기일"
                      value={formValues.deliveryDate}
                      onChange={handleChange('deliveryDate')}
                      required
                      error={formValues.deliveryDate === ''}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      name="validityPeriod"
                      fullWidth
                      label="견적 유효기간"
                      value={formValues.validityPeriod}
                      onChange={handleChange('validityPeriod')}
                      required
                      error={formValues.validityPeriod === ''}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion expanded={expanded2} onChange={handleAccordion2Toggle} sx={{ mb: 2 }} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant="subtitle1">추가 입력 필드</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* 1행: 협상금액 */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    name="negoAmount"
                    fullWidth
                    label="견적가 수기입력"
                    value={formValues.negoAmount}
                    onChange={handleNumericChange('negoAmount')}
                    // 숫자만 입력가능
                    inputProps={{         // 모바일 키보드 숫자 키만 표시
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                    // 회색 안내 문구
                    placeholder="최종견적가 수기입력 필요시에 기입"
                    // 빈 값일 때 helperText 표시
                    helperText={formValues.negoAmount === '' ? '최종견적가 수기입력 필요시에 기입' : ''}
                  />
                </Box>
                {/* 2행: 기타 사항 사용자가 입력한 내용 그대로 반영 엔터키 적용*/}
                <TextField
                  name="etc"
                  fullWidth
                  multiline
                  rows={3}
                  label="기타 사항"
                  value={formValues.etc}
                  onChange={handleChange('etc')}
                />
              </AccordionDetails>
            </Accordion>


            {/* 자재 입력 내용 */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6" gutterBottom> 자재 입력 내용 </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleMaterialClick}
                sx={{ mb: 2 }}
              >
                자재 선택
              </Button>
            </Box>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="materials">
                {provided => (
                  <Table
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    size="small"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>자재명</TableCell>
                        <TableCell>단위</TableCell>
                        <TableCell width="50px">수량</TableCell>
                        <TableCell width="150px">단가</TableCell>
                        <TableCell>비고</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(materialRows || []).map((row, index) => (
                        <Draggable key={row.id} draggableId={row.id.toString()} index={index}>
                          {(prov, snap) => (
                            <TableRow
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              style={prov.draggableProps.style}
                            >
                              {/* drag handle */}
                              <TableCell {...prov.dragHandleProps} sx={{ width: 40 }}>
                                <DragIndicatorIcon fontSize="small" />
                              </TableCell>
                              {/* 읽기전용 */}
                              <TableCell {...prov.dragHandleProps}>{row.materialName}</TableCell>
                              <TableCell {...prov.dragHandleProps}>{row.materialUnit}</TableCell>
                              {/* 인라인 편집 */}
                              <TableCell>
                                {row.materialId === 15 || row.materialId === 14 ? (
                                  row.quantity
                                
                                ) : (
                                  <TextField
                                    type="number"
                                    value={row.quantity}
                                    onChange={handleMaterialChange(row.id, 'quantity')}
                                    variant="standard"
                                    size="small"
                                    inputProps={{
                                      min: 1,
                                    }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {row.materialId === 15 || row.materialId === 14 ? (
                                  row.materialPrice
                                ) : (
                                  <TextField
                                    type="number"
                                    value={row.materialPrice}
                                    onChange={handleMaterialChange(row.id, 'materialPrice')}
                                    variant="standard"
                                    size="small"
                                    inputProps={{
                                      min: 1,
                                    }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <TextField
                                  value={row.note ?? ''}
                                  onChange={handleMaterialChange(row.id, 'note')}
                                  variant="standard"
                                  size="small"
                                  placeholder="비고"
                                  multiline
                                  maxRows={4}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton onClick={() => handleMaterialDelete(row.id)}>
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  </Table>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>

    {/* 수정 확인 다이얼로그 */}
    <Dialog
      open={confirmDialogOpen}
      onClose={() => setConfirmDialogOpen(false)}
    >
      <DialogTitle>저장하시겠습니까?</DialogTitle>
      <DialogContent dividers>
        <Typography>
          견적서 번호는 자동으로 관리되며, 기존 견적서는 수정되지 않고
          수정된 부분으로 “새로 작성” 되어 관리됩니다.<br/><br/>
          <span style={{ fontSize: '14px', color: 'red' }}>※ 잡자재, 노무비, 공구손료 등의 자재순서와 부가세 별도 여부 체크를 확인 해 주세요.</span>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDialogOpen(false)}>취소</Button>
        <Button variant="contained" onClick={handleConfirm}>확인</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={materialDialogOpen}
      onClose={handleMaterialDialogClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>자재 선택</DialogTitle>

      <DialogContent dividers sx={{ height: 600 }}>
        {/* 1) 선택된 자재 이름만 보이기 */}
        {modalSelectedMaterials.length > 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {modalSelectedMaterials.length} 개 선택됨
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            아직 선택된 자재가 없습니다.
          </Typography>
        )}

        {/* 전체 자재 그리드 (페이징, 삭제 숨기고, 체크박스만) */}
        <MaterialPage
          // 체크박스 활성화
          enableCheckbox
          // 페이징 비활성화
          disablePagination
          // 자재 선택 시 호출되는 핸들러
          onSelectionChange={setModalSelectedMaterials}
          // 초기 선택된 자재 목록
          initialSelection={modalSelectedMaterials}
          // 삭제 버튼 숨김
          hideDeleteAction
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleMaterialDialogClose}>취소</Button>
        <Button variant="contained" onClick={handleMaterialDialogConfirm}>
          확인
        </Button>
      </DialogActions>
    </Dialog>

    {/* 고객사 관리 모달 */}
    <AddClientModal
      open={openAddClient}
      onClose={() => setOpenAddClient(false)}
      onChange={(updatedList) => {
        // 모달에서 CRUD 후에 반환된 [{id,name},…] 배열을
        // Autocomplete 옵션용 문자열 배열로 변환
        const names = updatedList.map(c => c.clientName);
        setClientOptions(names);

        // 현재 선택된 값이 삭제된 경우 초기화
        if (!names.includes(formValues.clientCompany)) {
          handleChange('clientCompany')({ target: { value: '' } });
        }
      }}
      initialName={pendingClient}
    />
    
    </>
  );
};

export default EstimateWriteDialog; 